import OpenAI from 'openai';

/**
 * Initialize OpenAI client for resume parsing
 */
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Required for client-side usage
});

/**
 * Parse resume text using GPT-4 with structured output
 * @param {string} resumeText - Raw text extracted from resume
 * @returns {Promise<Object>} Structured resume data
 */
export async function parseResumeWithAI(resumeText) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert resume parser. Extract structured information from resumes including skills, experience, education, and determine seniority level based on years of experience and responsibilities. Be accurate and comprehensive.' 
        },
        { 
          role: 'user', 
          content: `Please parse this resume and extract structured information:\n\n${resumeText}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'resume_parsing_response',
          schema: {
            type: 'object',
            properties: {
              personal_info: {
                type: 'object',
                properties: {
                  name: { type: 'string' },
                  email: { type: 'string' },
                  phone: { type: 'string' },
                  location: { type: 'string' }
                }
              },
              skills: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    category: { type: 'string' },
                    proficiency: { type: 'string' },
                    years_experience: { type: 'number' }
                  }
                }
              },
              years_of_experience: { type: 'number' },
              seniority_level: { 
                type: 'string',
                enum: ['junior', 'mid', 'senior', 'lead', 'principal', 'executive']
              },
              work_experience: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    position: { type: 'string' },
                    company: { type: 'string' },
                    duration: { type: 'string' },
                    description: { type: 'string' },
                    responsibilities: {
                      type: 'array',
                      items: { type: 'string' }
                    }
                  }
                }
              },
              education: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    degree: { type: 'string' },
                    institution: { type: 'string' },
                    year: { type: 'string' },
                    gpa: { type: 'string' }
                  }
                }
              },
              certifications: {
                type: 'array',
                items: { type: 'string' }
              },
              languages: {
                type: 'array',
                items: { type: 'string' }
              },
              summary: { type: 'string' },
              confidence_score: { 
                type: 'number',
                minimum: 0,
                maximum: 1
              }
            },
            required: ['skills', 'years_of_experience', 'seniority_level', 'summary', 'confidence_score'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error parsing resume with AI:', error);
    throw new Error('Failed to parse resume with AI: ' + error.message);
  }
}

/**
 * Enhanced resume analysis with skill gap analysis
 * @param {string} resumeText - Raw resume text
 * @param {string} jobDescription - Job description to compare against
 * @returns {Promise<Object>} Enhanced analysis with skill gaps
 */
export async function analyzeResumeForJob(resumeText, jobDescription) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert recruiter and resume analyst. Compare resumes against job descriptions, identify skill matches, gaps, and provide hiring recommendations.' 
        },
        { 
          role: 'user', 
          content: `Analyze this resume against the job description and provide detailed insights:\n\nRESUME:\n${resumeText}\n\nJOB DESCRIPTION:\n${jobDescription}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'resume_job_analysis',
          schema: {
            type: 'object',
            properties: {
              overall_match_score: {
                type: 'number',
                minimum: 0,
                maximum: 100
              },
              skill_matches: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    skill: { type: 'string' },
                    match_level: { type: 'string', enum: ['exact', 'similar', 'transferable'] },
                    years_experience: { type: 'number' }
                  }
                }
              },
              skill_gaps: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    skill: { type: 'string' },
                    importance: { type: 'string', enum: ['critical', 'important', 'nice-to-have'] },
                    alternatives: {
                      type: 'array',
                      items: { type: 'string' }
                    }
                  }
                }
              },
              experience_assessment: {
                type: 'object',
                properties: {
                  level_match: { type: 'string', enum: ['under', 'match', 'over'] },
                  years_required: { type: 'number' },
                  years_candidate: { type: 'number' },
                  relevant_experience: { type: 'string' }
                }
              },
              strengths: {
                type: 'array',
                items: { type: 'string' }
              },
              concerns: {
                type: 'array',
                items: { type: 'string' }
              },
              recommendation: {
                type: 'object',
                properties: {
                  decision: { type: 'string', enum: ['strong_hire', 'hire', 'maybe', 'no_hire'] },
                  reasoning: { type: 'string' },
                  next_steps: {
                    type: 'array',
                    items: { type: 'string' }
                  }
                }
              }
            },
            required: ['overall_match_score', 'skill_matches', 'skill_gaps', 'recommendation'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing resume for job:', error);
    throw new Error('Failed to analyze resume for job: ' + error.message);
  }
}

/**
 * Enhanced OpenAI service with resume parsing functionality
 */
export const openaiService = {
  parseResume: parseResumeWithAI,
  analyzeResumeForJob: analyzeResumeForJob,
  
  // Legacy compatibility
  async parseResumeText(resumeText) {
    return parseResumeWithAI(resumeText);
  }
};

export default openai;